import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { useCart } from '../../hooks/useCart';
import SideMenu from '../categories/SideMenu';
import { ShoppingCart } from 'lucide-react';
import { toast } from 'react-toastify';

const PageLayout = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;

  @media (min-width: 768px) {
    flex-direction: row;
  }
`;

const SideMenuWrapper = styled.div`
  display: none;

  @media (min-width: 768px) {
    display: block;
    width: 200px;
    margin-right: 20px;
  }
`;

const ProductContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;

  @media (min-width: 768px) {
    flex-direction: row;
  }
`;

const ProductImage = styled.img`
  width: 100%;
  max-width: 400px;
  height: auto;
  object-fit: cover;
  margin-bottom: 20px;

  @media (min-width: 768px) {
    margin-right: 40px;
    margin-bottom: 0;
  }
`;

const ProductInfo = styled.div`
  flex: 1;
`;

const ProductName = styled.h1`
  font-size: 24px;
  margin-bottom: 10px;

  @media (min-width: 768px) {
    font-size: 28px;
  }
`;

const OriginalPrice = styled.p`
  font-size: 14px;
  color: #888;
  text-decoration: line-through;
  margin-bottom: 5px;
`;

const ProductPrice = styled.p`
  font-size: 20px;
  font-weight: bold;
  color: #4CAF50;
  margin-bottom: 15px;

  @media (min-width: 768px) {
    font-size: 24px;
    margin-bottom: 20px;
  }
`;

const MinPurchaseInfo = styled.p`
  font-size: 12px;
  color: #FF6347;
  margin-top: 5px;
`;

const ProductDescription = styled.p`
  font-size: 14px;
  line-height: 1.5;
  margin-bottom: 15px;

  @media (min-width: 768px) {
    font-size: 16px;
    margin-bottom: 20px;
  }
`;

const ProductMeta = styled.div`
  margin-bottom: 15px;

  @media (min-width: 768px) {
    margin-bottom: 20px;
  }
`;

const MetaItem = styled.p`
  font-size: 12px;
  margin-bottom: 3px;

  @media (min-width: 768px) {
    font-size: 14px;
    margin-bottom: 5px;
  }
`;

const WeightInputContainer = styled.div`
  margin-bottom: 15px;
  label {
    font-size: 14px;
    margin-right: 10px;
  }
`;

const WeightInput = styled.input`
  padding: 8px;
  width: 100px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 16px;
`;

const UnitSelect = styled.select`
  padding: 8px;
  font-size: 14px;
  margin-left: 10px;
`;

const QuantityInputContainer = styled.div`
  margin-bottom: 15px;
  label {
    font-size: 14px;
    margin-right: 10px;
  }
`;

const QuantityInput = styled.input`
  padding: 8px;
  width: 100px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 16px;
`;

const AddToCartButton = styled.button`
  display: flex;
  align-items: center;
  background-color: #4CAF50;
  color: white;
  padding: 10px 20px;
  font-size: 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s;
  width: 100%;
  justify-content: center;

  &:hover {
    background-color: #45a049;
  }

  @media (min-width: 768px) {
    width: auto;
    font-size: 18px;
  }

  svg {
    margin-right: 8px;
  }
`;

const ProductDetail = ({ showToast }) => {
  const [product, setProduct] = useState(null);
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { slug } = useParams();
  const { addToCart } = useCart();

  const [selectedWeight, setSelectedWeight] = useState(1);
  const [unit, setUnit] = useState('kg');
  const [quantity, setQuantity] = useState(1); // New state for quantity

  const unitLabels = {
    kg: 'Kg',
    g: 'Grams',
    l: 'Liters',
    ml: 'ml',
    units: 'Units',
  };

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await fetch(`/api/products/${slug}/`);
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const data = await response.json();
        setProduct(data);

        // Fetch promotions
        const promoResponse = await fetch('/api/specials/');
        const promoData = await promoResponse.json();
        setPromotions(promoData);

        // Set weight and unit based on product
        if (data.is_pre_packed) {
          setSelectedWeight(parseFloat(data.weight) || data.min_weight || 1); 
          setUnit(data.weight_unit || 'l'); // Set to liters for liquids
        } else {
          setSelectedWeight(data.min_weight || 1); // Set minimum weight to 1kg
          setUnit('kg'); // Default to kg for weight-based products
        }
        setLoading(false);
      } catch (e) {
        setError('Failed to fetch product details');
        setLoading(false);
      }
    };
    fetchProduct();
  }, [slug]);

  const getPromotionalPrice = (productId) => {
    for (const promo of promotions) {
      const promoProduct = promo.products.find(p => p.id === productId);
      if (promoProduct) {
        return promo;
      }
    }
    return null;
  };

  const handleAddToCart = () => {
    if (product) {
      const promo = getPromotionalPrice(product.id);
      const price = promo ? promo.promotional_price : product.price;
  
      const itemToAdd = {
        ...product,
        price,
        quantity: product.is_pre_packed ? quantity : undefined,
        selectedWeight: product.is_pre_packed ? undefined : selectedWeight,
        isPrePacked: product.is_pre_packed,
        unit,
        min_purchase_quantity: promo ? promo.min_purchase_quantity : null,
        min_purchase_unit: promo ? promo.min_purchase_unit : null
      };
  
      // Add to cart by either quantity or weight
      addToCart(itemToAdd, product.is_pre_packed ? quantity : selectedWeight);
      toast.success(`${product.name} added to cart!`);
      // showToast('Item added to cart', 'success');
    }
  };

  const handleWeightChange = (e) => {
    const promo = getPromotionalPrice(product.id);
    const minPurchaseQuantity = promo ? promo.min_purchase_quantity : product.min_weight || 1;

    let newWeight = parseFloat(e.target.value);
    if (isNaN(newWeight) || newWeight <= 0) {
      toast.error('Please enter a valid weight.');
      return;
    }

    if (newWeight < minPurchaseQuantity) {
      const unitLabel = unitLabels[unit] || unit;
      toast.error(`Minimum purchase of ${minPurchaseQuantity} ${unitLabel} required.`);
      newWeight = minPurchaseQuantity;
    }

    setSelectedWeight(newWeight);
  };

  // const handleQuantityChange = (e) => {
  //   const newQuantity = parseInt(e.target.value, 10);
  //   if (isNaN(newQuantity) || newQuantity < 1) {
  //     toast.error('Please enter a valid quantity.');
  //     return;
  //   }
  //   setQuantity(newQuantity);
  // };
  const handleQuantityChange = (e) => {
    const promo = getPromotionalPrice(product.id);
    const minPurchaseQuantity = promo ? promo.min_purchase_quantity : 1;
  
    let newQuantity = parseInt(e.target.value, 10);
    if (isNaN(newQuantity) || newQuantity < 1) {
      toast.error('Please enter a valid quantity.');
      return;
    }
  
    if (newQuantity < minPurchaseQuantity) {
      const unitLabel = unitLabels['units'] || 'Units';
      toast.error(`Minimum purchase of ${minPurchaseQuantity} ${unitLabel} required.`);
      newQuantity = minPurchaseQuantity;
    }
  
    setQuantity(newQuantity);
  };

  const calculatePrice = () => {
    if (!product) return '0.00';

    const promo = getPromotionalPrice(product.id);
    const basePrice = promo ? promo.promotional_price : product.price;

    return product.is_pre_packed
      ? (basePrice * quantity).toFixed(2) // Total price for prepacked items
      : (parseFloat(basePrice) * selectedWeight).toFixed(2); // Weight-based price
  };

  const calculatePricePerUnit = () => {
    if (!product) return '0.00';

    const promo = getPromotionalPrice(product.id);
    const basePrice = promo ? promo.promotional_price : product.price;

    return product.is_pre_packed && selectedWeight > 0
      ? (basePrice / selectedWeight).toFixed(2) // Price per unit for prepacked items
      : null;
  };

  if (loading) return <p>Loading product details...</p>;
  if (error) return <p>{error}</p>;
  if (!product) return <p>Product not found</p>;

  const promo = getPromotionalPrice(product.id);

  return (
    <PageLayout>
      <SideMenuWrapper>
        <SideMenu />
      </SideMenuWrapper>
      <ProductContainer>
        {product?.image && <ProductImage src={product.image} alt={product.name} />}
        <ProductInfo>
          <ProductName>{product?.name}</ProductName>
          {promo ? (
            <>
              <OriginalPrice>Original Price: ${product.price.toFixed(2)}</OriginalPrice>
              <ProductPrice>Discounted Price:${promo.promotional_price.toFixed(2)} ({calculatePricePerUnit()} per {unitLabels[unit]})</ProductPrice>
              {promo.min_purchase_quantity && (
                <MinPurchaseInfo>
                  Minimum purchase: {promo.min_purchase_quantity} {promo.min_purchase_unit}
                </MinPurchaseInfo>
              )}
            </>
          ) : (
            <ProductPrice>
             Price: ${product?.price?.toFixed(2)}({calculatePricePerUnit()} per {unitLabels[unit]})
            </ProductPrice>
          )}

          <ProductPrice>
            Selected: {product.is_pre_packed ? `${quantity} x ${selectedWeight} ${unitLabels[unit]}` : `${selectedWeight} ${unitLabels[unit]}`} - ${calculatePrice()}
          </ProductPrice>

          <ProductDescription>{product?.description}</ProductDescription>

          <ProductMeta>
            <MetaItem><strong>Stock:</strong> {product?.stock_quantity}</MetaItem>
          </ProductMeta>

          {product?.is_pre_packed ? (
            <QuantityInputContainer>
              <label htmlFor="quantity-input">Quantity:</label>
              <QuantityInput
                type="number"
                id="quantity-input"
                min={1}
                value={quantity}
                onChange={handleQuantityChange}
              />
            </QuantityInputContainer>
          ) : (
            <WeightInputContainer>
              <label htmlFor="weight-input">Select Weight:</label>
              <WeightInput
                type="number"
                id="weight-input"
                step="0.5"
                min={1}
                value={selectedWeight}
                onChange={handleWeightChange}
              />
              <UnitSelect value={unit} onChange={(e) => setUnit(e.target.value)}>
                <option value="kg">Kg</option>
                <option value="g">Grams</option>
                <option value="l">Liters</option>
              </UnitSelect>
            </WeightInputContainer>
          )}

          <AddToCartButton onClick={handleAddToCart}>
            <ShoppingCart size={22} />
            Add to Cart
          </AddToCartButton>
        </ProductInfo>
      </ProductContainer>
    </PageLayout>
  );
};

export default ProductDetail;